import { Link } from 'react-router-dom';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { theme } from '../../styles/theme';
import { Seo, faqJsonLd, breadcrumbJsonLd, orgJsonLd } from '../../lib/seo';

const FAQ = [
  {
    q: '¿Dónde comprar Lutron en México?',
    a: 'NULED es distribuidor mayorista Lutron en México con sede en CDMX. Distribuimos las líneas RadioRA 3, Wallbox (Skylark, Diva, Ariadni, Toggler) y Maestro (incluyendo sensores de ocupación). Cotización en 24 horas y entrega a toda la República para electricistas, integradores y arquitectos.',
  },
  {
    q: '¿Qué línea Lutron necesito para mi proyecto?',
    a: 'Para un dimmer individual sin sistema: Wallbox (Skylark Contour o Diva). Para residencial estándar con LEDs: Maestro CL. Para sensores de ocupación: Maestro MS-OPS. Para residencial inteligente con keypads, persianas y app: RadioRA 3. NULED te ayuda a especificar el modelo correcto según tu instalación.',
  },
  {
    q: '¿Qué precio tiene Lutron en México?',
    a: 'Los precios varían por línea. Wallbox parte desde $580 MXN. Maestro desde $720 MXN. RadioRA 3 (sistema) requiere cotización por proyecto desde $80,000 MXN. Como distribuidor mayorista, NULED ofrece precios competitivos para electricistas, integradores y distribuidores registrados.',
  },
  {
    q: '¿NULED instala Lutron?',
    a: 'NULED especifica, cotiza y entrega Lutron + luminarias LED como una sola solución. La instalación física la realizan electricistas e integradores certificados. Si no tienes instalador, podemos recomendarte uno cercano.',
  },
  {
    q: '¿Lutron funciona con luminarias LED dimmables?',
    a: 'Sí. Las líneas Maestro CL y Wallbox CL están diseñadas para LED dimmable (TRIAC, ELV, MLV). Las luminarias NULED dimmables están explícitamente probadas con cada protocolo Lutron para garantizar atenuación sin flicker.',
  },
  {
    q: '¿Cuánto tarda la entrega de Lutron?',
    a: 'Productos en stock (Wallbox, Maestro estándar, sensores básicos): 48 horas en CDMX, 5–7 días en el resto de México. Productos de orden especial (RadioRA 3 custom, sensores específicos): 4–6 semanas según configuración.',
  },
  {
    q: '¿Qué pasó con RA2 Select?',
    a: 'Lutron está discontinuando RA2 Select gradualmente y migrando a RadioRA 3, su sucesor moderno. Si compras hoy, recomendamos RadioRA 3 para que tu sistema esté vigente los próximos años. Lutron ofrece migración parcial — los keypads Pico, sensores y persianas son compatibles con ambos sistemas.',
  },
  {
    q: '¿Dónde comprar dimmers Lutron en México?',
    a: 'NULED es la opción mayorista para electricistas e integradores en México. Distribuimos toda la línea Wallbox y Maestro de Lutron a precio competitivo. Pregunta por nuestros tiers para distribuidores y profesionales.',
  },
];

type SystemCard = {
  code: string;
  name: string;
  tagline: string;
  for: string;
  products: string;
  restricted?: boolean;
};

const SYSTEMS: SystemCard[] = [
  {
    code: 'caseta',
    name: 'Caséta',
    tagline: 'Entrada residencial con app',
    for: 'Casas y departamentos. Hasta 50 dispositivos. Sin neutro.',
    products: 'Atenuadores PD-6WCL, Pico remotes, Smart Bridge Pro 2.',
  },
  {
    code: 'radiora3',
    name: 'RadioRA 3',
    tagline: 'Sistema RF residencial moderno',
    for: 'Residencial inteligente. Hasta 200 dispositivos.',
    products: 'Procesador RR-PROC2, atenuadores wireless, keypads Pico, sensores.',
  },
  {
    code: 'wallbox',
    name: 'Wallbox',
    tagline: 'Atenuadores individuales en pared',
    for: 'Residencial y comercial. Sin sistema requerido.',
    products: 'Skylark Contour, Diva, Ariadni, Toggler, Lyneo. TRIAC, ELV, CL.',
  },
  {
    code: 'maestro',
    name: 'Maestro',
    tagline: 'Atenuadores estándar profesionales',
    for: 'Residencial premium y comercial. Sensores de ocupación.',
    products: 'Maestro Estándar, CL, Wireless, Eco-dim, MS-OPS sensores.',
  },
  {
    code: 'vive',
    name: 'Vive',
    tagline: 'Sistema comercial wireless',
    for: 'Hoteles, oficinas, retail, restaurantes.',
    products: 'Hubs Vive, atenuadores wireless, sensores ocupación, software Vue.',
  },
  {
    code: 'homeworks',
    name: 'HomeWorks',
    tagline: 'Premium · Solo distribuidores certificados',
    for: 'Residencial de lujo. Vía integrador certificado Lutron.',
    products: 'Keypads Palladiom, atenuadores HQRD, shading Sivoia QS, integración AV.',
    restricted: true,
  },
];

export function LutronLanding() {
  return (
    <div style={{ background: theme.bg, color: theme.text, fontFamily: theme.font.sans }}>
      <Seo
        title="Lutron México · Distribuidor Mayorista | NULED"
        description="NULED es distribuidor mayorista Lutron en México. RadioRA 3, Wallbox y Maestro. Atenuadores, sensores de ocupación, sistemas RF residenciales. Cotización 24h, entrega a toda la República."
        keywords="lutron mexico, distribuidor lutron mexico, comprar lutron, radiora 3 mexico, lutron wallbox mexico, lutron maestro mexico, atenuador lutron, dimmer lutron pared, sensor ocupacion lutron"
        canonical="https://nuled.mx/lutron"
        jsonLd={[
          orgJsonLd,
          {
            '@context': 'https://schema.org',
            '@type': 'ProfessionalService',
            name: 'NULED — Distribuidor Mayorista Lutron México',
            description: 'Distribuidor mayorista Lutron en México. RadioRA 3, Wallbox y Maestro. Especificación, cotización e integración con luminarias LED NULED.',
            url: 'https://nuled.mx/lutron',
            areaServed: { '@type': 'Country', name: 'México' },
            brand: [
              { '@type': 'Brand', name: 'Lutron' },
              { '@type': 'Brand', name: 'NULED' },
            ],
            makesOffer: SYSTEMS.map(s => ({
              '@type': 'Offer',
              itemOffered: { '@type': 'Product', name: `Lutron ${s.name}`, brand: { '@type': 'Brand', name: 'Lutron' } },
            })),
          },
          breadcrumbJsonLd([
            { name: 'Inicio', url: 'https://nuled.mx/' },
            { name: 'Lutron México', url: 'https://nuled.mx/lutron' },
          ]),
          faqJsonLd(FAQ),
        ]}
      />

      <Header />

      {/* Hero */}
      <section style={{
        background: `linear-gradient(135deg, ${theme.brandDark} 0%, ${theme.brand} 100%)`,
        color: theme.textOnBrand,
      }}>
        <div style={{
          maxWidth: theme.maxWidth,
          margin: '0 auto',
          padding: '64px 24px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 40,
          alignItems: 'center',
        }}>
          <div>
            <div style={eyebrow}>Distribuidor mayorista · México</div>
            <h1 style={{
              fontFamily: theme.font.head,
              fontSize: 'clamp(36px, 5vw, 56px)',
              fontWeight: 300,
              lineHeight: 1.1,
              margin: '12px 0 20px',
              letterSpacing: -0.5,
            }}>
              Lutron México<br />
              <span style={{ fontWeight: 600 }}>de la mano de NULED</span>
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', maxWidth: 540, marginBottom: 28 }}>
              RadioRA 3, Wallbox y Maestro. Atenuadores, sensores de ocupación
              y sistemas RF residenciales. Cotizamos Lutron junto con tus
              luminarias LED en una sola propuesta.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a href="https://wa.me/525549332582?text=Hola%20NULED,%20quiero%20cotizar%20Lutron" style={ctaPrimary}>
                Cotizar mi proyecto
              </a>
              <Link to="/catalogo?brand=lutron" style={ctaSecondary}>
                Ver productos Lutron
              </Link>
            </div>
          </div>

          <div style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: 24,
            borderRadius: theme.radius.md,
          }}>
            <div style={{ fontSize: 11, color: theme.accent, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 16 }}>
              Líneas que distribuimos
            </div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
              {SYSTEMS.map(s => (
                <li key={s.code}>
                  <a href={`#${s.code}`} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: theme.textOnBrand,
                    textDecoration: 'none',
                    padding: '10px 0',
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    fontSize: 14,
                  }}>
                    <span style={{ fontWeight: 500 }}>{s.name}</span>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{s.tagline}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Wizard */}
      <section id="que-lutron-necesito" style={{
        maxWidth: theme.maxWidth,
        margin: '0 auto',
        padding: '64px 24px',
      }}>
        <div style={eyebrowInline}>Wizard rápido</div>
        <h2 style={h2}>¿Qué Lutron necesito?</h2>
        <p style={lede}>
          Cada instalación es distinta. Esta es la guía rápida que usamos cuando
          un electricista o integrador nos llama. Si tienes dudas, mándanos
          tus planos o foto del switch actual.
        </p>

        <div style={{
          marginTop: 32,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}>
          {[
            { headline: 'Un dimmer individual',          sys: 'Wallbox',    why: 'Skylark, Diva o Ariadni. Sin sistema.',      to: '/catalogo?brand=lutron&line=wallbox' },
            { headline: 'Dimmer + sensor ocupación',     sys: 'Maestro',    why: 'Maestro CL + MS-OPS para LED.',                to: '/catalogo?brand=lutron&line=maestro' },
            { headline: 'Residencial básico con app',    sys: 'Caséta',     why: 'Smart Bridge + Pico. Hasta 50 dispositivos.',  to: '/catalogo?brand=lutron&line=caseta' },
            { headline: 'Residencial inteligente',       sys: 'RadioRA 3',  why: 'Sistema completo con keypads + app.',          to: '/catalogo?brand=lutron&line=radiora3' },
            { headline: 'Comercial wireless',            sys: 'Vive',       why: 'Hoteles, oficinas, retail. Hubs por zona.',    to: '/catalogo?brand=lutron&line=vive' },
            { headline: 'No estoy seguro qué necesito',  sys: 'Asesoría',   why: 'Cuestionario guiado · 2 minutos.',             to: '/lutron/asesoria' },
          ].map(c => (
            <Link to={c.to} key={c.headline + c.sys} style={{
              display: 'block',
              padding: 24,
              border: `1px solid ${theme.border}`,
              background: theme.bg,
              textDecoration: 'none',
              color: theme.brand,
            }}>
              <div style={{ fontSize: 11, color: theme.accentDark, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                {c.headline}
              </div>
              <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 600, marginTop: 8 }}>
                {c.sys === 'Asesoría' ? 'Asesoría guiada' : `Lutron ${c.sys}`}
              </div>
              <div style={{ fontSize: 13, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                {c.why}
              </div>
              <div style={{ fontSize: 12, color: theme.accentDark, fontWeight: 700, marginTop: 16, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                {c.sys === 'Asesoría' ? 'Iniciar asesoría →' : 'Ver productos →'}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Sistemas en detalle */}
      <section style={{ background: theme.bgFooter, padding: '64px 24px' }}>
        <div style={{ maxWidth: theme.maxWidth, margin: '0 auto' }}>
          <div style={eyebrowInline}>Líneas Lutron</div>
          <h2 style={h2}>Lo que distribuimos</h2>
          <p style={{ ...lede, marginTop: 8, marginBottom: 24 }}>
            Mayoreo abierto para electricistas, integradores y distribuidores en
            Caséta, RadioRA 3, Wallbox, Maestro y Vive. <strong>HomeWorks</strong> se
            distribuye solo a integradores certificados Lutron — pregúntanos por
            el programa de certificación.
          </p>
          <div style={{ display: 'grid', gap: 12, marginTop: 24 }}>
            {SYSTEMS.map(s => (
              <article
                id={s.code}
                key={s.code}
                style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderLeft: `4px solid ${s.restricted ? theme.brandTag.lutron : theme.accent}`,
                  padding: 24,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: 24,
                  alignItems: 'start',
                }}
              >
                <div>
                  <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 600, color: theme.brand }}>
                    Lutron {s.name}
                  </div>
                  <div style={{ fontSize: 13, color: s.restricted ? theme.brandTag.lutron : theme.accentDark, fontWeight: 700, marginTop: 4, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    {s.tagline}
                  </div>
                  {s.restricted && (
                    <div style={{
                      display: 'inline-block',
                      marginTop: 10,
                      fontSize: 10,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      padding: '4px 10px',
                      background: 'rgba(192,132,252,0.12)',
                      color: theme.brandTag.lutron,
                      border: `1px solid ${theme.brandTag.lutron}`,
                    }}>
                      ★ Solo distribuidores certificados
                    </div>
                  )}
                </div>
                <div>
                  <div style={dl}>
                    <span style={dt}>Indicado para</span>
                    <span style={dd}>{s.for}</span>
                  </div>
                  <div style={{ ...dl, marginTop: 12 }}>
                    <span style={dt}>Productos</span>
                    <span style={dd}>{s.products}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {s.restricted ? (
                    <>
                      <a
                        href={`https://wa.me/525549332582?text=${encodeURIComponent('Hola NULED, me interesa el programa de distribuidor certificado HomeWorks')}`}
                        style={ctaSmallPrimary}
                      >
                        Pregúntanos por el programa
                      </a>
                      <Link to={`/lutron/${s.code}`} style={ctaSmallSecondary}>
                        Más info ↗
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link to={`/catalogo?brand=lutron&line=${s.code}`} style={ctaSmallPrimary}>
                        Ver productos {s.name} →
                      </Link>
                      <Link to={`/lutron/${s.code}`} style={ctaSmallSecondary}>
                        Más info ↗
                      </Link>
                    </>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Por qué NULED */}
      <section style={{ maxWidth: theme.maxWidth, margin: '0 auto', padding: '64px 24px' }}>
        <div style={eyebrowInline}>Por qué NULED</div>
        <h2 style={h2}>Mayorista Lutron + luminarias LED en una sola fuente</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 24,
          marginTop: 32,
        }}>
          {[
            ['Distribuidor mayorista oficial', 'Línea completa Lutron disponible. Stock local y orden directa a fábrica.'],
            ['Cotización integrada', 'Lutron + luminarias NULED en un solo PDF, no dos.'],
            ['Compatibilidad probada', 'Cada luminaria NULED dimmable está probada con dimmers Lutron CL.'],
            ['Entrega rápida', 'Wallbox y Maestro en stock. CDMX 48h, resto del país 5–7 días.'],
            ['Garantía y soporte', '1–2 años Lutron + soporte técnico en español vía NULED.'],
            ['Precio mayorista', 'Tier transparente para electricistas, integradores y distribuidores registrados.'],
          ].map(([title, desc]) => (
            <div key={title} style={{
              padding: 16,
              borderTop: `2px solid ${theme.accent}`,
            }}>
              <div style={{ fontFamily: theme.font.head, fontSize: 16, fontWeight: 600, color: theme.brand, marginBottom: 8 }}>
                {title}
              </div>
              <div style={{ fontSize: 13, color: theme.textMuted, lineHeight: 1.55 }}>
                {desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Re-anclar a NULED */}
      <section style={{ background: theme.brand, color: theme.textOnBrand, padding: '48px 24px' }}>
        <div style={{
          maxWidth: theme.maxWidth,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 32,
          alignItems: 'center',
        }}>
          <div>
            <div style={{ ...eyebrow, color: theme.accent }}>Pero antes que nada</div>
            <h2 style={{
              fontFamily: theme.font.head,
              fontSize: 32,
              fontWeight: 400,
              margin: '8px 0 12px',
              lineHeight: 1.15,
            }}>
              NULED importa y especifica <strong>iluminación LED</strong>.
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', marginBottom: 24 }}>
              Importamos y especificamos luminarias LED desde 2014. Lutron es nuestra extensión natural —
              cuando una luminaria necesita controlarse bien con dimmer profesional. Pero el catálogo NULED
              es lo que nos hace.
            </p>
            <Link to="/catalogo" style={{ ...ctaPrimary, display: 'inline-block' }}>
              Ver catálogo NULED
            </Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {[
              ['180+', 'productos NULED'],
              ['12 años', 'mayoreo en México'],
              ['CRI 90', 'estándar en luminarias'],
              ['1 año', 'garantía total'],
            ].map(([n, l]) => (
              <div key={l} style={{
                padding: 16,
                background: 'rgba(255,255,255,0.06)',
                borderLeft: `3px solid ${theme.accent}`,
              }}>
                <div style={{ fontFamily: theme.font.head, fontSize: 28, fontWeight: 600 }}>{n}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
                  {l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ maxWidth: 920, margin: '0 auto', padding: '64px 24px' }}>
        <div style={eyebrowInline}>Preguntas frecuentes</div>
        <h2 style={h2}>Lutron en México · Dudas comunes</h2>
        <div style={{ marginTop: 32 }}>
          {FAQ.map(({ q, a }, i) => (
            <details
              key={i}
              style={{
                borderTop: i === 0 ? `1px solid ${theme.border}` : 'none',
                borderBottom: `1px solid ${theme.border}`,
                padding: '4px 0',
              }}
            >
              <summary style={{
                cursor: 'pointer',
                padding: '18px 4px',
                fontSize: 16,
                fontWeight: 600,
                color: theme.brand,
                fontFamily: theme.font.head,
                listStyle: 'none',
                position: 'relative',
                paddingRight: 32,
              }}>
                {q}
                <span style={{ position: 'absolute', right: 4, top: 18, color: theme.accentDark, fontSize: 18 }}>+</span>
              </summary>
              <div style={{ padding: '0 4px 18px', fontSize: 14, color: theme.textMuted, lineHeight: 1.7 }}>
                {a}
              </div>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}

const eyebrow: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: theme.accent,
  fontWeight: 700,
};

const eyebrowInline: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: 2,
  textTransform: 'uppercase',
  color: theme.accentDark,
  fontWeight: 700,
};

const h2: React.CSSProperties = {
  fontFamily: theme.font.head,
  fontSize: 36,
  fontWeight: 400,
  color: theme.brand,
  margin: '8px 0 16px',
  lineHeight: 1.15,
};

const lede: React.CSSProperties = {
  fontSize: 16,
  color: theme.textMuted,
  lineHeight: 1.6,
  maxWidth: 640,
  margin: 0,
};

const ctaPrimary: React.CSSProperties = {
  background: theme.accent,
  color: theme.brand,
  padding: '14px 28px',
  fontSize: 14,
  fontWeight: 700,
  textDecoration: 'none',
  borderRadius: theme.radius.sm,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  display: 'inline-block',
};

const ctaSecondary: React.CSSProperties = {
  border: '1px solid rgba(255,255,255,0.4)',
  color: '#FFFFFF',
  padding: '14px 28px',
  fontSize: 14,
  fontWeight: 500,
  textDecoration: 'none',
  borderRadius: theme.radius.sm,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const ctaSmallPrimary: React.CSSProperties = {
  background: theme.brand,
  color: theme.textOnBrand,
  padding: '8px 16px',
  fontSize: 12,
  fontWeight: 700,
  textDecoration: 'none',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  textAlign: 'center',
};

const ctaSmallSecondary: React.CSSProperties = {
  border: `1px solid ${theme.border}`,
  color: theme.brand,
  padding: '8px 16px',
  fontSize: 12,
  fontWeight: 500,
  textDecoration: 'none',
  textAlign: 'center',
};

const dl: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: 4,
};

const dt: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: theme.textMuted,
  fontWeight: 700,
};

const dd: React.CSSProperties = {
  fontSize: 14,
  color: theme.brand,
  lineHeight: 1.5,
};
